import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup, TextField, Button, Divider, Chip, CircularProgress } from "@mui/material";
import { quoteTrade, placeTrade } from "../../services/tradingApi";
import { makeClientOrderId } from "../../services/functionsClient";
import { auth, db } from "../../components/firebase";
import { doc, onSnapshot } from "firebase/firestore";

const GLASS = {
  borderSoft: "1px solid rgba(51,65,85,.8)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const fmtUsd = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
};

export default function PredictionTradingTerminal({
  market,
  marketId,
  initialSide = "yes",
  initialStake = "100",
  onTradeSuccess,
}) {
  const [side, setSide] = useState(initialSide);
  const [stake, setStake] = useState(String(initialStake));

  const [maxSlippageBps, setMaxSlippageBps] = useState(150); // 1.5%
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState("");

  // баланс пользователя (users/{uid}.balance.usdc)
  const [usdcBalance, setUsdcBalance] = useState(null);

  const status = String(market?.status || "open").toLowerCase();
  const tradable = status === "open" || status === "approved"; // если у вас approved = открыт

  const yesPrice = Number(market?.yesPrice ?? market?.priceYes ?? 0);
  const noPrice = Number(market?.noPrice ?? market?.priceNo ?? (yesPrice ? 1 - yesPrice : 0));

  const activePrice = side === "yes" ? yesPrice : noPrice;

  const nStake = useMemo(() => Math.max(0, Number(stake) || 0), [stake]);

  // расчёты терминала (локально) — для UX (это не “заглушка”, это отображение)
  const contracts = nStake > 0 && activePrice > 0 ? nStake / activePrice : 0;
  const maxPayout = contracts * 1;
  const maxProfit = maxPayout - nStake;

  // подписка на баланс
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;

    const ref = doc(db, "users", u.uid);
    return onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      const bal = d.balance || {};
      setUsdcBalance(Number(bal.usdc ?? bal.USDC ?? bal.usdt ?? null));
    });
  }, []);

  // quote debounce
  useEffect(() => {
    let alive = true;
    setErr("");
    setQuote(null);

    if (!marketId || !tradable) return;
    if (!nStake || nStake <= 0) return;

    const t = setTimeout(async () => {
      try {
        setQuoting(true);
        const q = await quoteTrade({
          marketId,
          side,
          amountIn: nStake,
          maxSlippageBps,
        });
        if (alive) setQuote(q);
      } catch (e) {
        if (alive) setErr(e?.message || "Quote failed");
      } finally {
        if (alive) setQuoting(false);
      }
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [marketId, side, nStake, maxSlippageBps, tradable]);

  const disabledByBalance =
    typeof usdcBalance === "number" ? nStake > usdcBalance : false;

  async function confirmTrade() {
    setErr("");
    try {
      if (!tradable) throw new Error("Market is not open");
      if (!nStake || nStake <= 0) throw new Error("Invalid stake amount");
      if (disabledByBalance) throw new Error("Insufficient balance");

      setPlacing(true);

      const clientOrderId = makeClientOrderId("trade");
      const res = await placeTrade({
        marketId,
        side,
        amountIn: nStake,
        clientOrderId,
        maxSlippageBps,
      });

      onTradeSuccess?.(res);
      // после сделки можно сбросить quote, чтобы пересчитался
      setQuote(null);
    } catch (e) {
      setErr(e?.message || "Trade failed");
    } finally {
      setPlacing(false);
    }
  }

  const impliedYes = yesPrice > 0 ? Math.min(100, Math.max(0, yesPrice * 100)) : null;
  const impliedNo = noPrice > 0 ? Math.min(100, Math.max(0, noPrice * 100)) : null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: GLASS.text }}>
          Trading terminal
        </Typography>

        <Chip
          size="small"
          label={tradable ? "OPEN" : status.toUpperCase()}
          sx={{
            ml: "auto",
            height: 22,
            fontSize: 11,
            borderRadius: 999,
            color: tradable ? "#22c55e" : GLASS.subtext,
            border: GLASS.borderSoft,
            bgcolor: "rgba(15,23,42,.95)",
            "& .MuiChip-label": { px: 1.0, fontWeight: 900 },
          }}
        />
      </Box>

      <ToggleButtonGroup
        value={side}
        exclusive
        onChange={(_, next) => next && setSide(next)}
        fullWidth
        sx={{
          mb: 1.5,
          background: "rgba(15,23,42,.98)",
          borderRadius: 999,
          border: GLASS.borderSoft,
          "& .MuiToggleButton-root": {
            flex: 1,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 800,
            color: GLASS.subtext,
            border: "none",
            "&.Mui-selected": { color: "#020617", bgcolor: "#22c55e" },
            "&:first-of-type": { borderRadius: "999px 0 0 999px" },
            "&:last-of-type": { borderRadius: "0 999px 999px 0" },
          },
        }}
      >
        <ToggleButton value="yes">
          Yes {Number.isFinite(impliedYes) ? `${Math.round(impliedYes)}¢` : ""}
        </ToggleButton>
        <ToggleButton value="no">
          No {Number.isFinite(impliedNo) ? `${Math.round(impliedNo)}¢` : ""}
        </ToggleButton>
      </ToggleButtonGroup>

      <Typography sx={{ fontSize: 12, color: GLASS.subtext, mb: 1 }}>
        Balance:{" "}
        <Typography component="span" sx={{ color: GLASS.text, fontWeight: 800 }}>
          {typeof usdcBalance === "number" ? fmtUsd(usdcBalance) : "—"}
        </Typography>
      </Typography>

      <TextField
        label="Stake (USDC)"
        fullWidth
        value={stake}
        onChange={(e) => setStake(e.target.value.replace(/[^\d.]/g, ""))}
        disabled={!tradable || placing}
        InputProps={{
          sx: {
            fontSize: 13,
            bgcolor: "rgba(15,23,42,.96)",
            borderRadius: 2,
            "& fieldset": { borderColor: "rgba(148,163,184,.7)" },
            "&:hover fieldset": { borderColor: GLASS.borderSoft },
            "&.Mui-focused fieldset": { borderColor: GLASS.borderSoft },
          },
        }}
        InputLabelProps={{ sx: { fontSize: 12, color: GLASS.subtext } }}
      />

      <Box sx={{ mt: 1.1, display: "flex", gap: 1 }}>
        {[25, 100, 250].map((v) => (
          <Button
            key={v}
            variant="outlined"
            onClick={() => setStake(String(v))}
            disabled={!tradable || placing}
            sx={{
              flex: 1,
              textTransform: "none",
              fontSize: 11,
              borderRadius: 2,
              borderColor: "rgba(148,163,184,.6)",
              color: GLASS.subtext,
            }}
          >
            ${v}
          </Button>
        ))}
      </Box>

      <Box sx={{ mt: 1.2 }}>
        <TextField
          label="Max slippage (%)"
          fullWidth
          value={(maxSlippageBps / 100).toFixed(2)}
          onChange={(e) => {
            const pct = Number(e.target.value) || 0;
            const bps = Math.round(pct * 100);
            setMaxSlippageBps(Math.max(0, Math.min(5000, bps)));
          }}
          disabled={!tradable || placing}
          InputProps={{
            sx: {
              fontSize: 13,
              bgcolor: "rgba(15,23,42,.96)",
              borderRadius: 2,
              "& fieldset": { borderColor: "rgba(148,163,184,.7)" },
            },
          }}
          InputLabelProps={{ sx: { fontSize: 12, color: GLASS.subtext } }}
        />
      </Box>

      <Divider sx={{ my: 1.5, borderColor: "rgba(30,64,175,.6)" }} />

      {/* Quote / Order ticket */}
      <Box sx={{ fontSize: 12, color: GLASS.subtext }}>
        <Row label="Contracts" value={contracts.toFixed(4)} />
        <Row label="Entry (spot UI)" value={activePrice ? fmtUsd(activePrice) : "—"} />
        <Row label="Max payout" value={fmtUsd(maxPayout)} valueColor="#22c55e" />
        <Row label="Max profit" value={fmtUsd(maxProfit)} valueColor={maxProfit >= 0 ? "#22c55e" : "#f97373"} />
      </Box>

      <Box sx={{ mt: 1.4, p: 1.2, borderRadius: 2, border: "1px solid rgba(255,255,255,.10)", bgcolor: "rgba(15,23,42,.55)" }}>
        <Typography sx={{ fontWeight: 900, fontSize: 12, mb: 0.8, color: GLASS.text }}>
          Engine quote
        </Typography>

        {quoting ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>Calculating…</Typography>
          </Box>
        ) : quote ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6, fontSize: 12 }}>
            <Typography sx={{ color: GLASS.subtext }}>Price</Typography>
            <Typography sx={{ textAlign: "right", color: GLASS.text, fontWeight: 800 }}>{fmtUsd(quote.price)}</Typography>

            <Typography sx={{ color: GLASS.subtext }}>Tokens out</Typography>
            <Typography sx={{ textAlign: "right", color: GLASS.text, fontWeight: 800 }}>
              {Number(quote.tokensOut || 0).toFixed(6)}
            </Typography>

            <Typography sx={{ color: GLASS.subtext }}>Fee</Typography>
            <Typography sx={{ textAlign: "right", color: GLASS.text, fontWeight: 800 }}>{fmtUsd(quote.fee)}</Typography>

            <Typography sx={{ color: GLASS.subtext }}>Slippage</Typography>
            <Typography sx={{ textAlign: "right", color: GLASS.text, fontWeight: 800 }}>
              {((quote.slippageBps || 0) / 100).toFixed(2)}%
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>
            Enter stake to receive quote from engine.
          </Typography>
        )}
      </Box>

      {err ? (
        <Typography sx={{ mt: 1.2, color: "error.main", fontSize: 12 }}>
          {err}
        </Typography>
      ) : null}

      {disabledByBalance ? (
        <Typography sx={{ mt: 1.2, color: "#f97373", fontSize: 12 }}>
          Insufficient balance for this stake.
        </Typography>
      ) : null}

      <Button
        variant="contained"
        fullWidth
        onClick={confirmTrade}
        disabled={!tradable || placing || quoting || !quote || !nStake || disabledByBalance}
        sx={{
          mt: 1.7,
          py: 1,
          fontWeight: 900,
          fontSize: 14,
          textTransform: "none",
          borderRadius: 2,
          background: "linear-gradient(135deg, #22c55e, #3b82f6)",
          boxShadow: "0 14px 34px rgba(34,197,94,.55)",
          "&:hover": { background: "linear-gradient(135deg, #16a34a, #2563eb)" },
        }}
      >
        {placing ? "Placing…" : "Confirm trade"}
      </Button>
    </Box>
  );
}

function Row({ label, value, valueColor }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.45 }}>
      <Typography sx={{ color: "#9ca3af", fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color: valueColor || "#e5e7eb", fontWeight: 800, fontSize: 12 }}>{value}</Typography>
    </Box>
  );
}
